import { type ReactNode } from 'react';
import React, { createContext, useCallback, useEffect, useState } from 'react';

import { type Feature } from './featureFlags';
import { featureFlags } from './featureFlags';

type FeatureFlagsState = Record<Feature, boolean>;

interface FeatureFlagsContextType {
    flags: FeatureFlagsState;
    isFeatureEnabled: (feature: Feature) => boolean;
    toggleFeature: (feature: Feature) => void;
    setFeature: (feature: Feature, enabled: boolean) => void;
}

export const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

const getInitialState = (): FeatureFlagsState => {
    const initialState: Record<string, boolean> = {};
    for (const key in featureFlags) {
        const featureKey = key as Feature;
        initialState[featureKey] = featureFlags[featureKey].defaultValue;
    }

    try {
        const storedFlags = localStorage.getItem('featureFlags');
        if (storedFlags) {
            const parsedFlags = JSON.parse(storedFlags);
            for (const key in initialState) {
                const featureKey = key as Feature;
                if (typeof parsedFlags[featureKey] === 'boolean') {
                    initialState[featureKey] = parsedFlags[featureKey];
                }
            }
        }
    } catch (error) {
        console.error('Error reading feature flags from localStorage', error);
    }

    return initialState as FeatureFlagsState;
};

export const FeatureFlagProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [flags, setFlags] = useState<FeatureFlagsState>(getInitialState);

    useEffect(() => {
        try {
            localStorage.setItem('featureFlags', JSON.stringify(flags));
        } catch (error) {
            console.error('Error saving feature flags to localStorage', error);
        }
    }, [flags]);

    const isFeatureEnabled = useCallback((feature: Feature) => !!flags[feature], [flags]);

    const toggleFeature = useCallback((feature: Feature) => {
        setFlags((prevFlags) => ({ ...prevFlags, [feature]: !prevFlags[feature] }));
    }, []);

    const setFeature = useCallback((feature: Feature, enabled: boolean) => {
        setFlags((prevFlags) => ({ ...prevFlags, [feature]: enabled }));
    }, []);

    return (
        <FeatureFlagsContext.Provider value={{ flags, isFeatureEnabled, toggleFeature, setFeature }}>
            {children}
        </FeatureFlagsContext.Provider>
    );
};
